<script setup lang="ts">
import {
  $approveLineItem, $lineItem, $lineItemAnnotationCreate, $lineItemAnnotationDelete, $lineItemAnnotationList,
  $lineItemAnnotationUpdate, $lineItemAnnotationCommentCreate, $getNextPreviousLineItem, $getLineItemWfPreviousStatuses
} from '@/api/brandings'
import VuePdfEmbed from 'vue-pdf-embed'
import { can } from '@layouts/plugins/casl'
import ability from '@/plugins/casl/ability'
import { VDataTableVirtual } from 'vuetify/labs/VDataTable'
import { useSnackbarStore } from '@/store/snackbar'
import type { ComponentPublicInstance } from 'vue'
import { downloadAllFilesUsingLinks } from '@/helpers/utils/downloadAllFilesUsingLinks'

interface IProps {
  projectId?: string | number,
  workOrderId?: string | number,
  lineItemId?: string | number,
  isGlobalSearch?: boolean,
  searchQuery?: string,
  searchByQuery?: string,
  isReviewDialog?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  projectId: '',
  workOrderId: '',
  lineItemId: '',
  isGlobalSearch: false,
  searchQuery: '',
  searchByQuery: '',
  isReviewDialog: false,
})

interface Emit {
  (e: 'onApprovalAction'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const router = useRouter()
const route = useRoute()

const artworksBlock = ref<ComponentPublicInstance | null>(null)  // DOM element

const lineItem = ref<any>({})
const annotations = ref<any[]>([])
const showAnnotationsSidebar = ref<boolean>(false)
const showAnnotationCommentDialog = ref<boolean>(false)
const parentAnnotation = ref<any | null>(null)
const artworkFileLoading = ref<boolean>(true)
const realEstateLoading = ref<boolean>(true)
const prePressProofLoading = ref<boolean>(true)
const artworkFilePageCount = ref<number>(1)
const realEstatePageCount = ref<number>(1)
const prePressProofPageCount = ref<number>(1)
const artworkFileCurrentPage = ref<number>(1)
const realEstateCurrentPage = ref<number>(1)
const prePressProofCurrentPage = ref<number>(1)
const isMounted = ref<boolean>(false)
const pdfScale = ref<number>(100)
const isMoving = ref<boolean>(false)
const artworkFileMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const realEstateMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const prePressProofMapPos = ref<{ left: number, top: number, x: number, y: number }>({ left: 0, top: 0, x: 0, y: 0 })
const isMapCenter = ref<boolean>(false)
const isLineItemLoaded = ref<boolean>(false)
const artworkComparisonSelect = ref<0 | 1 | 2>(0)
const artworkComparisons = ref([
  { value: 0, label: 'Artwork Link - PrePress Proof' },
  { value: 1, label: 'Artwork Link - Scope PDF' },
  { value: 2, label: 'PrePress Proof - Scope PDF' }
])
const lineItemStatuses = ref([])

const downloadAllFilesLoading = ref<boolean>(false)

const approveBtnLoading = ref<boolean>(false)
const rejectBtnLoading = ref<boolean>(false)

const previousStatusList = ref<any[]>([])
const previousStatusListLoading = ref<boolean>(false)

const isCanAddAnnotation = computed<boolean>(() => {
  return ability.can('add', 'brandingartworkannot')
})

const artworkFileUrl = computed<string | null>(() => {
  return getDropboxArtworkData(lineItem.value, 0, true)
})

const realEstateUrl = computed<string | null>(() => {
  return getDropboxArtworkData(lineItem.value, 4, true)
})

const prePressProofUrl = computed<string | null>(() => {
  return getDropboxArtworkData(lineItem.value, 3, true)
})

const prodAiUrl = computed<string | null>(() => {
  return getDropboxArtworkData(lineItem.value, 1, true)
})

// Computed property to check if all URLs are empty
const isDisabledDownloadAllLink = computed<boolean>(() => {
  const urls = [
    artworkFileUrl.value,
    prePressProofUrl.value,
    realEstateUrl.value,
    prodAiUrl.value
  ]

  // Check if all URLs are empty or invalid
  return !urls.some(url => url) // Returns true if all URLs are empty
})

const isShownAnyAnnotationMenu = computed<boolean>(() => {
  return !!annotations.value
    .filter((annot: any) => annot.page === artworkFileCurrentPage.value && !!annot.isShownAnnotationMenu).length
})

const isDisabledApproveBtn = computed(() => {
  return !!annotations.value.filter((annot: any) => !annot.is_resolved).length
})

watch(artworkComparisonSelect, () => {
  pdfScale.value = 100
})

const getLineItem = async () => {
  try {
    const res = await $lineItem(props.lineItemId)
    lineItem.value = res.data

    // show PDFs with actual url(if exist)
    if (artworkFileUrl.value && prePressProofUrl.value) {
      artworkComparisonSelect.value = 0
    }
    else if (artworkFileUrl.value && realEstateUrl.value) {
      artworkComparisonSelect.value = 1
    }
    else if (prePressProofUrl.value && realEstateUrl.value) {
      artworkComparisonSelect.value = 2
    }
    else if (artworkFileUrl.value || prePressProofUrl.value) {
      artworkComparisonSelect.value = 0
    }
    else if (realEstateUrl.value) {
      artworkComparisonSelect.value = 1
    }
  } catch (e: any) {
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  }
}

const approveLineItem = async (isApprove = true, rejectStatusId = null) => {
  if (!props.lineItemId) return

  if (isApprove) {
    approveBtnLoading.value = true
  } else {
    rejectBtnLoading.value = true
  }

  try {
    await $approveLineItem(props.lineItemId?.toString(), {
      is_approve: isApprove,
      reject_status_id: rejectStatusId ? rejectStatusId : undefined
    })
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item updated successfully!'
    })
    await getLineItem()

    emit('onApprovalAction')
  } catch (e: any) {
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    if (isApprove) {
      approveBtnLoading.value = false
    } else {
      rejectBtnLoading.value = false
    }
  }
}

const rejectLineItem = async (rejectStatusId = null) => {
  await approveLineItem(false, rejectStatusId)
}

const getLineItemWfPreviousStatuses = async () => {
  if (!props.lineItemId) return

  previousStatusListLoading.value = true
  try {
    const res = await $getLineItemWfPreviousStatuses(props.lineItemId?.toString())
    previousStatusList.value = res.data
  } catch (e: any) {
    const mes = e.response.data ? e.response.data : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    previousStatusListLoading.value = false
  }
}

const handleRejectMenuOpen = (isOpen: boolean) => {
  if (isOpen) {
    getLineItemWfPreviousStatuses()
  }
}

const setHover = (annotation: any, status: boolean) => {
  annotation['hover'] = status
}

const hideAnnotationMenus = () => {
  annotations.value = annotations.value.map(i => ({ ...i, isShownAnnotationMenu: false }) )
}

const getAnnotations = async () => {
  try {
    const res = await $lineItemAnnotationList({
      line_item: props.lineItemId
    })
    annotations.value = res.data.results
  } catch (e: any) {
    console.log(e)
  }
}

const createAnnotation = async (event: MouseEvent) => {
  const textLayer = document.getElementsByClassName('textLayer')[0] as HTMLElement
  if (
    event.target === textLayer
    && !(event.target instanceof HTMLElement && event.target.hasAttribute('annot'))
    && !isShownAnyAnnotationMenu.value
    && isCanAddAnnotation.value
  ) {
    const topPerc = (event.offsetY - 12) / textLayer.offsetHeight * 100
    const leftPerc = (event.offsetX - 12) / textLayer.offsetWidth * 100
    const payload = {
      line_item: props.lineItemId,
      top: topPerc,
      left: leftPerc,
      page: artworkFileCurrentPage.value
    }
    try {
      const res = await $lineItemAnnotationCreate(payload)
      annotations.value.push(res.data)
    } catch (e: any) {
      console.log(e)
    }
  } else if (isShownAnyAnnotationMenu.value) {
    hideAnnotationMenus()
  }
}

const updateAnnotation = async (annotation: any) => {
  const ref = 'pop' + annotation.id
  try {
    await $lineItemAnnotationUpdate(annotation.id, annotation)
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation saved!'
    })
  } catch (e: any) {
    console.log(e)
  }
}

const deleteAnnotation = async (id: number, key: number) => {
  try {
    await $lineItemAnnotationDelete(id)
    annotations.value = annotations.value.filter(ann => ann.id !== id)
    hideAnnotationMenus()
  } catch (e: any) {
    console.log(e)
  }
}

const createComment = async (comment: string) => {
  const payload = {
    line_item: props.lineItemId,
    content: comment,
    annotation: parentAnnotation.value.id
  }
  try {
    await $lineItemAnnotationCommentCreate(payload)
    showAnnotationCommentDialog.value = false
    parentAnnotation.value = null
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Comment saved!'
    })
    await getAnnotations()
  } catch (e: any) {
    console.log(e)
  }
}

const previousLineItem = async () => {
  if (!props.lineItemId) return

  const payload = {
    is_previous: true,
    search: props.searchQuery,
    search_by: props.searchByQuery
  } as { is_previous: boolean, search: string, search_by: string, work_order_id?: string | number | null | undefined }
  if (!props.isGlobalSearch) {
    payload.work_order_id = lineItem.value.work_order
  }
  try {
    const res = await $getNextPreviousLineItem(props.lineItemId?.toString(), payload)
    const requestStatus = res.status
    const nextLineItem = res.data

    await redirectToNextPreviousLineItem(requestStatus, nextLineItem, true)
  } catch (e: any) {
    console.log(e)
  }
}

const nextLineItem = async () => {
  if (!props.lineItemId) return

  const payload = {
    search: props.searchQuery,
    search_by: props.searchByQuery
  } as { search: string, search_by: string, work_order_id?: string | number | null | undefined }
  if (!props.isGlobalSearch) {
    payload.work_order_id = lineItem.value?.work_order
  }
  try {
    const res = await $getNextPreviousLineItem(props.lineItemId?.toString(), payload)
    const requestStatus = res.status
    const nextLineItem = res.data

    await redirectToNextPreviousLineItem(requestStatus, nextLineItem)
  } catch (e: any) {
    console.log(e)
  }
}

const redirectToNextPreviousLineItem = async (requestStatus: any, nextLineItem: any, isPrevious = false as boolean) => {
  // If next line item request status - 204 'No Content' -> redirect to line item list page
  if (requestStatus === 204) {
    const snackBarText = `${isPrevious ? 'Previous' : 'Next'} line item not found`
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: snackBarText
    })
  } else {
    if (props.isGlobalSearch) {
      await router.push({
        path: `/projects/branding/global-search-line-items/${nextLineItem.id}`,
        query: { search: props.searchQuery, 'search-by': props.searchByQuery } })
    } else {
      await router.push({
        path: `/projects/branding/${props.projectId}/work-orders/${props.workOrderId}/line-items/${nextLineItem.id}`,
        query: { search: props.searchQuery, 'search-by': props.searchByQuery }
      })
    }
  }
}

const onMoveMapStart = (e: MouseEvent, mapPos: any, elementId: string) => {
  // On mouse left button (primary) - move PDF
  if (e.button === 0) {
    const el = document.getElementById(elementId) as HTMLElement
    isMoving.value = true
    mapPos.left = el.scrollLeft
    mapPos.top = el.scrollTop
    mapPos.x = e.clientX
    mapPos.y = e.clientY
  }
}

const onMoveMap = (e: MouseEvent, isCenter: boolean, mapPos: any, elementId: string) => {
  if (isCenter) {
    setTimeout(() => {
      const containerIds = ['artwork-file-pdf-container-id', 'real-estate-pdf-container-id',
        'prepress-proof-pdf-container-id']
      containerIds.forEach(containerId => {
        const container = document.getElementById(containerId) as HTMLElement
        container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2
        container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
      })
    }, 0)
  } else {
    const el = document.getElementById(elementId) as HTMLElement
    const dx = e.clientX - mapPos.x
    const dy = e.clientY - mapPos.y

    el.scrollTop = mapPos.top - dy
    el.scrollLeft = mapPos.left - dx
  }
}

const getDropboxArtworkData = (item: any, artworkType: number, isUrl = false as boolean): string | null => {
  const dropboxArtwork = item.dropbox_artworks ? item.dropbox_artworks.find((i: any) => i.type === artworkType) : null
  if (dropboxArtwork) {
    return isUrl ? dropboxArtwork.url : dropboxArtwork.file_modified_date
  } else {
    return null
  }
}

const downloadAllFiles = async () => {
  downloadAllFilesLoading.value = true

  // Filter out empty/null/undefined links
  const urls: string[] = [
    artworkFileUrl.value,
    prePressProofUrl.value,
    realEstateUrl.value,
    prodAiUrl.value
  ].filter((url): url is string => Boolean(url))

  if (!urls.length) {
    snackbarStore.showMessage({
      color: 'warning',
      icon: 'mdi-alert-circle',
      title: 'Warning',
      text: 'No valid links found'
    })
    downloadAllFilesLoading.value = false
  }

  try {
    const result = await downloadAllFilesUsingLinks(urls)

    if (result.status === 'complete') {
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Download Complete',
        text: `All ${result.successCount} file(s) downloaded successfully!`
      })
    }
    else if (result.status === 'partial') {
      snackbarStore.showMessage({
        color: 'warning',
        icon: 'mdi-alert',
        title: 'Partial Download',
        text: `${result.successCount} of ${result.total} file(s) downloaded (${result.failedCount} failed)`
      })
    }
    else { // status === 'error'
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Download Failed',
        text: 'Failed to process download request'
      })
    }
  } catch (e: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: 'Failed to process download requests'
    })

    console.log('Download error:', e)
  } finally {
    downloadAllFilesLoading.value = false
  }
}

const initReviewBlock = () => {
  getLineItem()
  getAnnotations()

  // set max height to Artworks Block(with PDFs)
  if (artworksBlock.value) {
    const windowHeight = window.innerHeight
    const elementRect = artworksBlock.value.$el.getBoundingClientRect()
    // Where 50(title height + padding) and 72(48 - footer height + 24 - layout padding)
    const blockPadding = props.isReviewDialog ? 50 : 72
    const remainingHeight = windowHeight - elementRect.top - blockPadding  //
    artworksBlock.value.$el.style.maxHeight = remainingHeight + 'px'
  }
}

// Expose the function to parent components
defineExpose({
  initReviewBlock
})

onMounted(() => {
  if (!props.isReviewDialog) {
    initReviewBlock()
  }
})

const getLineItemListLink = () => {
  if (props.isGlobalSearch) {
    return {
      path: '/projects/branding/global-search-line-items',
      query: { search: props.searchQuery, 'search-by': props.searchByQuery }
    }
  } else {
    return {
      path: `/projects/branding/${props.projectId}/work-orders/${props.workOrderId}`,
      query: { search: props.searchQuery, 'search-by': props.searchByQuery }
    }
  }
}
</script>

<template>
  <div>
    <AnnotationsSidebar
      v-model="showAnnotationsSidebar"
      v-model:annotations="annotations"
      :current-page="artworkFileCurrentPage"
      :artwork-select="artworkComparisonSelect"
      @onSetHover="setHover($event.annotation, $event.status)"
      @onComment="parentAnnotation = $event; showAnnotationCommentDialog = true"
    />
    <AnnotationCommentDialog
      v-model="showAnnotationCommentDialog"
      :parent-annotation="parentAnnotation"
      @onSubmit="createComment($event)"
    />
    <VCard>
      <VContainer fluid>
        <VRow>
          <VCol class="py-0 d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex justify-start align-center flex-wrap">
              <VBtn
                v-if="!isReviewDialog"
                color="light"
                size="small"
                class="px-0 text-decoration-none text-high-emphasis"
                :to="getLineItemListLink()"
              >
                <VIcon
                  start
                  icon="mdi-arrow-left"
                />
                Back
              </VBtn>
              <div>
                <h3 class="ml-3">
                  Artwork Review
                </h3>
              </div>
              <div
                v-if="!isReviewDialog"
              >
                <VBtn
                  size="small"
                  class="px-2 ma-1"
                  color="secondary"
                  @click="previousLineItem"
                >
                  <VIcon
                    icon="mdi-arrow-left"
                  />
                  Previous
                </VBtn>
                <VBtn
                  size="small"
                  class="px-2 ma-1"
                  color="secondary"
                  @click="nextLineItem"
                >
                  Next
                  <VIcon
                    icon="mdi-arrow-right"
                  />
                </VBtn>
              </div>
              <div class="d-flex align-center flex-wrap">
                <template v-if="lineItem.is_can_user_approve">
                  <template v-if="!lineItem.is_on_hold">
                    <div class="position-relative">
                      <VMenu
                        v-if="isDisabledApproveBtn"
                        activator="parent"
                        location="top center"
                        open-on-hover
                        open-delay="100"
                        close-delay="0"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span class="px-2">
                          Annotations not resolved
                        </span>
                      </VMenu>
                      <VBtn
                        size="small"
                        class="px-2 ma-1"
                        color="success"
                        :disabled="isDisabledApproveBtn"
                        @click="approveLineItem"
                      >
                        <VIcon
                          icon="tabler-check"
                        />
                        Approve
                        <VProgressCircular
                          v-if="!!approveBtnLoading"
                          indeterminate
                          size="16"
                          width="2"
                          class="ml-1"
                          color="light"
                        />
                      </VBtn>
                    </div>
                    <VBtn
                      v-if="lineItem?.is_released"
                      :disabled="!!rejectBtnLoading"
                      size="small"
                      class="mt-1 mt-sm-0"
                      @click="rejectLineItem(null)"
                    >
                      <VIcon
                        icon="tabler-x"
                      />
                      Reject
                      <VProgressCircular
                        v-if="!!rejectBtnLoading"
                        indeterminate
                        size="16"
                        width="2"
                        class="ml-1"
                        color="light"
                      />
                    </VBtn>
                    <VMenu
                      v-else
                      location="bottom center"
                      open-on-click
                      @update:modelValue="handleRejectMenuOpen"
                    >
                      <template #activator="{ props }">
                        <VBtn
                          :disabled="!!rejectBtnLoading"
                          size="small"
                          class="px-2 ma-1"
                          v-bind="props"
                        >
                          <VIcon
                            icon="tabler-x"
                          />
                          Reject
                          <VProgressCircular
                            v-if="!!rejectBtnLoading"
                            indeterminate
                            size="16"
                            width="2"
                            class="ml-1"
                            color="light"
                          />
                        </VBtn>
                      </template>
                      <VCard class="position-relative reject-previous-statuses-menu-block">
                        <VOverlay
                          v-model="previousStatusListLoading"
                          persistent
                          absolute
                          contained
                          class="align-center justify-center"
                        >
                          <VProgressCircular
                            indeterminate
                            size="16"
                            width="2"
                            color="primary"
                          />
                        </VOverlay>
                        <div
                          v-if="!previousStatusList.length && !previousStatusListLoading"
                          class="px-2 py-3"
                        >
                          Previous statuses not found
                        </div>
                        <VList
                          v-else
                          class="px-1"
                          :class="{'pa-5': previousStatusListLoading}"
                          nav
                        >
                          <div
                            v-if="previousStatusList.length"
                            class="text-body-2 text-center no-pointer-events"
                          >
                            Back to:
                          </div>
                          <template
                            v-for="(status, statusKey) in previousStatusList"
                            :key="statusKey"
                          >
                            <VListItem
                              :title="status?.name"
                              class="border-t rounded-t-0"
                              @click="rejectLineItem(status?.id)"
                            />
                          </template>
                        </VList>
                      </VCard>
                    </VMenu>
                  </template>
                  <VAlert
                    v-else
                    show
                    variant="tonal"
                    color="warning"
                    class="text-body-2 py-1"
                  >
                    Items flagged as "On Hold"<br>cannot be Approved/Rejected
                  </VAlert>
                </template>
              </div>
            </div>
            <div class="d-flex align-center flex-wrap">
              <VSelect
                v-model="artworkComparisonSelect"
                :items="[...artworkComparisons]"
                item-title="label"
                item-value="value"
                density="compact"
                class="mr-2"
              />
              <VBtn
                size="small"
                class="my-1"
                color="secondary"
                append-icon="mdi-menu"
                @click="showAnnotationsSidebar = true"
              >
                Annotations
              </VBtn>
            </div>
          </VCol>
        </VRow>
        <VRow>
          <VCol
            class="py-1"
            cols="12"
            sm="5"
            md="4"
          >
            {{ lineItem.description }}
            <template v-if="can('view', 'finances')">
              <div class="pt-2">
                <b>Total Price:</b> {{ lineItem.total }}
              </div>
            </template>
          </VCol>
          <VCol
            class="py-1 d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <template v-if="lineItem?.responsible_list?.length">
              <table>
                <tr
                  v-for="(responsible, responsibleKey) in lineItem?.responsible_list"
                  :key="responsibleKey"
                >
                  <td
                    v-if="responsibleKey === 0"
                    :rowspan="lineItem?.responsible_list?.length"
                  >
                    <div class="d-flex flex-column">
                      Responsible:
                      <VBtn
                        v-if="lineItem?.approve_phases?.length"
                        color="light"
                        size="x-small"
                        class="px-1"
                      >
                        <VMenu
                          activator="parent"
                          location="top center"
                          open-on-hover
                          open-delay="100"
                          close-delay="0"
                          offset="5"
                          class="default-tooltip text-body-2"
                        >
                          <VDataTableVirtual
                            :headers="[
                              { key: 'responsible_approver_name', title: 'Approver', sortable: false },
                              { key: 'is_approved', title: 'Is Approved', sortable: false },
                            ]"
                            :items="[...lineItem?.approve_phases]"
                            no-data-text="Approve Info not found"
                            hover
                            density="compact"
                          >
                            <template #item.is_approved="{ item }">
                              <span v-if="item.raw.is_approved === true">
                                <VIcon
                                  color="success"
                                  icon="tabler-check"
                                />
                                by
                                {{ item.raw.actual_approver_name }}
                              </span>
                              <span v-else-if="item.raw.is_approved === false">
                                <VIcon
                                  color="primary"
                                  icon="tabler-x"
                                />
                                by
                                {{ item.raw.actual_approver_name }}
                              </span>
                              <span v-else>
                                <VIcon
                                  color="secondary"
                                  icon="tabler-minus"
                                />
                              </span>
                            </template>
                          </VDataTableVirtual>
                        </VMenu>
                        <VIcon
                          size="22"
                          icon="tabler-info-circle"
                        />
                      </VBtn>
                    </div>
                  </td>
                  <td class="pl-1">
                    {{ responsible?.first_name }} {{ responsible?.last_name }}
                  </td>
                </tr>
              </table>
            </template>
            <template v-else>
              <span>
                Responsible:
                {{ lineItem?.responsible_name }}
              </span>
            </template>
          </VCol>
          <VCol
            class="py-1 d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            Assigned To: {{ lineItem.assigned_to_name }}
          </VCol>
        </VRow>
        <VRow class="mt-4">
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <VBtn
              v-if="isCanAddAnnotation"
              icon="tabler-info-circle"
              size="small"
              variant="elevated"
              density="compact"
              class="rounded-sm mr-2"
              color="info"
            >
              <VIcon
                size="22"
                icon="tabler-info-circle"
              />
              <VTooltip
                activator="parent"
                location="top"
                offset="5"
                class="default-tooltip"
              >
                <span>
                  To add an annotation right-click on the PDF
                </span>
              </VTooltip>
            </VBtn>
            Line No: {{ lineItem.line_no }}
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <span class="mr-1">Status name:</span>
            <span
              :class="{'rejected-back-status-color': !!lineItem?.is_rejected_back_status}"
            >
              {{ lineItem.item_status_info ? lineItem.item_status_info.line_item_status_name : '' }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="4"
            md="3"
          >
            <span class="mr-1">On Hold:</span>
            <VChip
              :color="lineItem.is_on_hold ? 'warning' : 'secondary'"
            >
              {{ lineItem.is_on_hold ? 'Yes' : 'No' }}
            </VChip>
          </VCol>
          <VCol
            class="d-flex align-center py-0"
            cols="12"
            sm="12"
            md="3"
          >
            <VSlider
              v-model="pdfScale"
              :max="500"
              :min="100"
              :step="10"
              label="Scale, %"
              hide-details
              class="pdf-scale-slider"
            >
              <template v-slot:append>
                <VTextField
                  v-model="pdfScale"
                  type="number"
                  style="width: 80px"
                  density="compact"
                  hide-details
                  variant="outlined"
                  class="pa-1"
                />
              </template>
            </VSlider>
          </VCol>
        </VRow>
        <VRow class="mt-0">
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Artwork:
            </span>
            <a
              target="_blank"
              :href="artworkFileUrl"
            >
              Download
            </a>
            <span
              v-if="getDropboxArtworkData(lineItem, 0)"
              class="ml-1"
            >
              -
              {{ getDropboxArtworkData(lineItem, 0) }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Prepress Proof:
            </span>
            <a
              target="_blank"
              :href="prePressProofUrl"
            >
              Download
            </a>
            <span
              v-if="getDropboxArtworkData(lineItem, 3)"
              class="ml-1"
            >
              -
              {{ getDropboxArtworkData(lineItem, 3) }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Scope PDF:
            </span>
            <a
              target="_blank"
              :href="realEstateUrl"
            >
              Download
            </a>
            <span
              v-if="getDropboxArtworkData(lineItem, 4)"
              class="ml-1"
            >
              -
              {{ getDropboxArtworkData(lineItem, 4) }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              Prod AI:
            </span>
            <a
              target="_blank"
              :href="prodAiUrl"
            >
              Download
            </a>
            <span
              v-if="getDropboxArtworkData(lineItem, 1)"
              class="ml-1"
            >
              -
              {{ getDropboxArtworkData(lineItem, 1) }}
            </span>
          </VCol>
          <VCol
            class="d-flex align-center justify-start"
            cols="12"
            sm="6"
            md=""
          >
            <span class="mr-1">
              All Images:
            </span>
            <a
              class="position-relative"
              target="_blank"
              :href="isDisabledDownloadAllLink ? null : '#'"
              @click.prevent="downloadAllFiles"
            >
              Download
              <VOverlay
                :model-value="!!downloadAllFilesLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  size="16"
                  width="2"
                  indeterminate
                />
              </VOverlay>
            </a>
          </VCol>
        </VRow>
        <VRow
          ref="artworksBlock"
          class="artwork-row mx-0"
        >
          <VCol
            v-show="[0, 1].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': artworkFileLoading }"
          >
            <div
              v-if="artworkFileUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                v-model="artworkFileLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="artworkFileCurrentPage === 1"
                  @click="artworkFileCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ artworkFileCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="artworkFileCurrentPage === artworkFilePageCount"
                  @click="artworkFileCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="artwork-file-pdf-container-id"
                class="artwork-file-pdf-container"
                @mousedown="onMoveMapStart($event, artworkFileMapPos, 'artwork-file-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, artworkFileMapPos, 'artwork-file-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-file-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                  @click.right.prevent="(event) => { createAnnotation(event) }"
                >
                  <VuePdfEmbed
                    ref="ArtworkFilePdf"
                    id="artwork-file-pdf"
                    textLayer
                    :source="artworkFileUrl"
                    :page="artworkFileCurrentPage"
                    @rendered="artworkFileLoading = false; artworkFilePageCount = $refs.ArtworkFilePdf.pageCount"
                  />
                  <div
                    v-for="(annotation, key) in annotations.filter(annot => annot.page === artworkFileCurrentPage)"
                    :id="`annotation${key}`"
                    :key="key"
                    class="pdfAnnotation"
                    :class="{ 'hovered': annotation.hover, 'd-none': artworkFileLoading }"
                    :style="{ left: annotation.left + '%', top: annotation.top + '%' }"
                    annot="true"
                    @mouseover="setHover(annotation, true)"
                    @mouseout="setHover(annotation, false)"
                  >
                    <span annot="true">
                      {{ annotation.index }}
                    </span>
                    <VMenu
                      v-model="annotation.isShownAnnotationMenu"
                      :id="'pop' + annotation.id"
                      :activator="`#annotation${key}`"
                      :open-on-click="true"
                      :open-on-hover="false"
                      :close-on-content-click="false"
                      location="top"
                      offset="5"
                    >
                      <VCard>
                        <VCardText
                          class="border rounded-lg pa-3"
                        >
                          <VTextarea
                            v-model="annotation.comment"
                            placeholder="Enter comments..."
                            class="mb-1"
                            rows="3"
                            :disabled="annotation.is_resolved || !can('change', 'brandingartworkannot')"
                          />
                          <div
                            v-if="!annotation.is_resolved"
                            class="mt-1 d-flex"
                          >
                            <div class="save-btn">
                              <VBtn
                                v-if="can('change', 'brandingartworkannot')"
                                color="success"
                                :disabled="!annotation.comment"
                                @click="updateAnnotation(annotation)"
                              >
                                Save
                              </VBtn>
                            </div>
                            <VTooltip
                              v-if="!annotation.comment"
                              activator=".save-btn"
                              location="top"
                              offset="5"
                              class="default-tooltip"
                            >
                              <span>
                                Please, add comment
                              </span>
                            </VTooltip>
                            <div class="set-resolved-btn">
                              <VBtn
                                v-if="can('resolve', 'brandingartworkannot')"
                                class="ml-1"
                                color="info"
                                :disabled="!annotation.comment"
                                @click="annotation.is_resolved=true; updateAnnotation(annotation)"
                              >
                                Set Resolved
                              </VBtn>
                            </div>
                            <VTooltip
                              v-if="!annotation.comment"
                              activator=".set-resolved-btn"
                              location="top"
                              offset="5"
                              class="default-tooltip"
                            >
                              <span>
                                Please, add comment
                              </span>
                            </VTooltip>
                            <VBtn
                              v-if="can('delete', 'brandingartworkannot')"
                              class="ml-1"
                              @click="deleteAnnotation(annotation.id, key)"
                            >
                              Delete
                            </VBtn>
                          </div>
                        </VCardText>
                      </VCard>
                    </VMenu>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="!artworkFileUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
          <VCol
            v-show="[0, 2].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': prePressProofLoading }"
          >
            <div
              v-if="prePressProofUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                v-model="prePressProofLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="prePressProofCurrentPage === 1"
                  @click="prePressProofCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ prePressProofCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="prePressProofCurrentPage === prePressProofPageCount"
                  @click="prePressProofCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="prepress-proof-pdf-container-id"
                class="artwork-pdf-container"
                @mousedown="onMoveMapStart($event, prePressProofMapPos, 'prepress-proof-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, prePressProofMapPos, 'prepress-proof-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                >
                  <VuePdfEmbed
                    ref="PrePressProofPdf"
                    id="prepress-proof"
                    :source="prePressProofUrl"
                    :page="prePressProofCurrentPage"
                    @rendered="prePressProofLoading = false; prePressProofPageCount = $refs.PrePressProofPdf.pageCount"
                  />
                </div>
              </div>
            </div>
            <div
              v-else-if="!prePressProofUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
          <VCol
            v-show="[1, 2].includes(artworkComparisonSelect)"
            class="position-relative py-0 px-1"
            :class="{ 'min-height-200': realEstateLoading }"
          >
            <div
              v-if="realEstateUrl"
              style="margin: auto; max-height: inherit"
            >
              <VOverlay
                v-model="realEstateLoading"
                persistent
                absolute
                contained
                class="align-center justify-center"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                />
              </VOverlay>
              <div class="text-center">
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="realEstateCurrentPage === 1"
                  @click="realEstateCurrentPage--"
                >
                  <VIcon icon="mdi-arrow-left-bold-circle-outline" />
                </VBtn>
                <span>
                    {{ realEstateCurrentPage }}
                  </span>
                <VBtn
                  size="sm"
                  class="p-0 rounded-circle"
                  pill
                  color="light"
                  :disabled="realEstateCurrentPage === realEstatePageCount"
                  @click="realEstateCurrentPage++"
                >
                  <VIcon icon="mdi-arrow-right-bold-circle-outline" />
                </VBtn>
              </div>
              <div
                id="real-estate-pdf-container-id"
                class="artwork-pdf-container"
                @mousedown="onMoveMapStart($event, realEstateMapPos, 'real-estate-pdf-container-id')"
                v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, realEstateMapPos, 'real-estate-pdf-container-id') } }"
                @mouseup="isMoving = false"
                @mouseout="isMoving = false"
              >
                <div
                  class="artwork-pdf-wrapper overflow-hidden"
                  :style="{ width: `${pdfScale}%` }"
                >
                  <VuePdfEmbed
                    ref="RealEstatePdf"
                    id="real-estate-pdf"
                    :source="realEstateUrl"
                    :page="realEstateCurrentPage"
                    @rendered="realEstateLoading = false; realEstatePageCount = $refs.RealEstatePdf.pageCount"
                  />
                </div>
              </div>
            </div>
            <div
              v-else-if="!realEstateUrl"
              style="margin: auto;"
            >
              <h4>File not provided</h4>
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </VCard>
  </div>
</template>

<style lang="scss">
.artwork-file-pdf-container {
  overflow: scroll;
  max-width: 100%;
  max-height: inherit;
  .artwork-file-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
    .pdfAnnotation {
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      background: #dd082b;
      border-radius: 50%;
      text-align: center;
      color: #fff;
      cursor: pointer;
      &.hovered {
        background: #fff;
        color: #dd082b;
        border: 1px solid #dd082b;
      }
    }
  }
}

.artwork-pdf-container {
  overflow: scroll;
  max-width: 100%;
  max-height: inherit;
  .artwork-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
  }
}

canvas {
  width: 100% !important;
  height: unset !important;
}

.artwork-row {
  position: relative;
  > .v-col {
    max-height: inherit;
  }
}

.pdf-scale-slider {
  input {
    padding: 0 5px !important;
  }
}

a {
  color: rgb(var(--v-theme-secondary));
  cursor: not-allowed;
  &:link {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

.reject-previous-statuses-menu-block {
  box-shadow: 0 3px 17px -10px #5e5e5e !important;
}

.rejected-back-status-color {
  color: rgb(var(--v-theme-primary));
}
</style>
